import { createContext, useContext } from 'react'

import type { ResourceOption } from './resource-search-input'

// ---------------------------------------------------------------------------
// Resource lookup context - lets admin code provide search functions
// without coupling the shared input component to admin-specific API
// instances.  Each lookup provider is keyed by a string (e.g.
// "partnership", "product", "customer") so multiple resource types can
// be searched from a single context.
// ---------------------------------------------------------------------------

export interface ResourceLookupProvider {
  /**
   * Search for resources matching a term.
   *
   * `params` carries optional per-field filters drawn from the field's
   * `configuration_data.provider_params` (e.g. `{ category_slug }`). A
   * provider closure may also inject its own filters (e.g. a live year from
   * the surrounding form state). Providers that take no filters can ignore it.
   */
  search: (
    term: string,
    params?: Record<string, unknown>,
  ) => Promise<ResourceOption[]>
}

/** Registry of lookup providers keyed by provider name. */
export type ResourceLookupRegistry = Record<string, ResourceLookupProvider>

const ResourceLookupContext = createContext<ResourceLookupRegistry | null>(null)

/** Wrap a form tree to provide resource search capabilities. */
export function ResourceLookupContextProvider({
  providers,
  children,
}: {
  providers: ResourceLookupRegistry
  children: React.ReactNode
}) {
  return (
    <ResourceLookupContext.Provider value={providers}>
      {children}
    </ResourceLookupContext.Provider>
  )
}

/** Get a specific resource lookup provider by key. Returns null if not found. */
// eslint-disable-next-line react-refresh/only-export-components
export function useResourceLookup(
  providerKey: string | undefined,
): ResourceLookupProvider | null {
  const registry = useContext(ResourceLookupContext)
  if (!registry || !providerKey) return null
  return registry[providerKey] ?? null
}
